/**
 * What the cluster offers a namespace, constants of the platform shared by
 * the quota (`environmentQuota.ts`: the cluster's ceiling) and the
 * descriptors (`descriptor.ts`: no reservation of a pod beyond what one node
 * can give).
 */

/** CPU in millicores, memory in Mi. */
export interface Resources {
  cpu: number;
  memory: number;
}

/**
 * The nodes the cluster's autoscaler goes up to (the `odoo-project`
 * template's « Haute disponibilité » header). An environment whose pods
 * could never all be scheduled is refused.
 */
export const CLUSTER_MAX_NODES = 9;

/**
 * What ONE pod may reserve on a node of the cluster — measured on
 * mks-ludo-mks-dev on 2026-10-16 (kubectl, read-only): its b3-8 nodes
 * allocate 1840 m and 6054196Ki (5912 Mi); the DaemonSets reserve 242 m and
 * 422 Mi on every node (traefik 100 m / 64 Mi, victoria-logs vector
 * 100 m / 256 Mi, csi-nfs-node 30 m / 60 Mi, node-exporter 10 m / 32 Mi,
 * apiserver-proxy 2 m / 10 Mi). A pod reserving more than the rest is never
 * scheduled. Measure again when the flavour or the DaemonSets change.
 */
export const NODE_ALLOCATABLE: Resources = { cpu: 1840, memory: 5912 };
export const DAEMONSET_REQUESTS: Resources = { cpu: 242, memory: 422 };

const floorTo = (value: number, step: number) =>
  Math.floor(value / step) * step;

/**
 * The rest, rounded down to 10 m and 64 Mi: 1590 m, 5440 Mi — the maximum
 * reservations of the Odoo descriptor's admin bounds.
 */
export const POD_RESERVATION_MAX: Resources = {
  cpu: floorTo(NODE_ALLOCATABLE.cpu - DAEMONSET_REQUESTS.cpu, 10),
  memory: floorTo(NODE_ALLOCATABLE.memory - DAEMONSET_REQUESTS.memory, 64),
};

/** What the cluster may schedule at most, for `quotaProblems`. */
export const CLUSTER_REQUESTS_MAX: Resources = {
  cpu: CLUSTER_MAX_NODES * POD_RESERVATION_MAX.cpu,
  memory: CLUSTER_MAX_NODES * POD_RESERVATION_MAX.memory,
};
